// HSE Compliance screen: WTR breaches, EU working-time and driving limits, FRMS log.
// Port of the prototype's renderCompliance.

import React from 'react';
import { GROUPS, REGIONS, WORKERS, wArr, workersInRegion } from '../engine/data';
import type { Worker } from '../engine/data';
import { lrmTone } from '../engine/scores';
import { genInsight } from '../engine/insights';
import { AiInsight } from '../AiPanels';
import { scopedWorkers, useDash } from '../state';
import { Banner, Grid, Kpi, KpiRow, Panel, Pill, SecHeader, dataTable, fedTone, toneText } from '../ui';
import type { Tone } from '../ui';
import { cn } from '@/lib/utils';
import { GroupPicker, RegionPicker, StatTile, WorkerPicker } from './scopePickers';

const FRMS_LOG: { date: string; worker: string; type: string; by: string; status: string; tone: Tone }[] = [
  {
    date: '16 Oct',
    worker: 'J. Hartley',
    type: 'OT extension',
    by: 'S. Manager',
    status: 'Pending review',
    tone: 'warn',
  },
  { date: '15 Oct', worker: 'R. Patel', type: 'WTR waiver', by: 'Ops Lead', status: 'Escalated', tone: 'bad' },
  { date: '15 Oct', worker: 'F. Osei', type: 'WTR waiver', by: 'Ops Lead', status: 'Escalated', tone: 'bad' },
  { date: '14 Oct', worker: 'Y. Adebayo', type: 'OT extension', by: 'S. Manager', status: 'Authorised', tone: 'good' },
  {
    date: '13 Oct',
    worker: 'C. Johansson',
    type: 'OT extension',
    by: 'S. Manager',
    status: 'Authorised',
    tone: 'good',
  },
  {
    date: '12 Oct',
    worker: 'R. Patel',
    type: 'Split shift approval',
    by: 'Depot Mgr',
    status: 'Authorised',
    tone: 'good',
  },
];

const ComplianceKpis: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const wtrB = workers.filter((w) => w.wtrFlag).length;
  const over48 = workers.filter((w) => w.shiftHrs > 48).length;
  const over55 = workers.filter((w) => w.shiftHrs > 55).length;
  const driveRisk = workers.filter((w) => w.driveKm > 700).length;
  return (
    <KpiRow className='mb-4'>
      <Kpi
        label='WTR Breaches'
        value={wtrB}
        tone={wtrB ? 'bad' : 'good'}
        sub='11h rest violations'
        subTrend={wtrB ? 'up' : undefined}
      />
      <Kpi label='Workers >48h/wk' value={over48} tone={over48 ? 'warn' : 'good'} sub='EU Working Time' />
      <Kpi label='Workers >55h/wk' value={over55} tone={over55 ? 'bad' : 'good'} sub='Near 56h EU cap' subTrend='up' />
      <Kpi label='EU Drive Risk' value={driveRisk} tone={driveRisk ? 'bad' : 'good'} sub='Weekly km >700' />
    </KpiRow>
  );
};

const Th: React.FC<{ num?: boolean; center?: boolean; children: React.ReactNode }> = ({ num, center, children }) => (
  <th className={cn(dataTable.th, num && dataTable.num, center && 'text-center')}>{children}</th>
);

const ComplianceTables: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const { drillWorker } = useDash();
  const { td, num, tr } = dataTable;
  const row = (w: Worker) => ({ onClick: () => drillWorker(w.init), className: cn(tr, 'cursor-pointer') });
  const wtrList = workers.filter((w) => w.wtrFlag);
  const drivers = workers.filter((w) => w.driveKm > 0).sort((a, b) => b.driveKm - a.driveKm);
  return (
    <>
      <Grid cols={2} className='mb-4'>
        <Panel title='WTR Breaches' sub='11h minimum rest between shifts · LRM shows readiness impact'>
          {wtrList.length ? (
            <div className={dataTable.wrap}>
              <table className={dataTable.table}>
                <thead>
                  <tr>
                    <Th>Worker</Th>
                    <Th num>Rest Hrs</Th>
                    <Th num>FED</Th>
                    <Th num>LRM</Th>
                    <Th>Consec</Th>
                    <Th>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {wtrList.map((w) => (
                    <tr key={w.init} {...row(w)}>
                      <td className={td}>
                        <div className='font-semibold'>{w.name}</div>
                        <div className='text-[11px] text-muted-foreground'>{GROUPS[w.group].name}</div>
                      </td>
                      <td className={cn(td, num, 'font-semibold text-destructive')}>{w.restHrs}h</td>
                      <td className={cn(td, num, toneText[fedTone(w.fed)])}>{w.fed}</td>
                      <td className={cn(td, num, 'font-semibold', toneText[lrmTone(w.lrm)])}>
                        {w.lrm > 0 ? `+${w.lrm}` : w.lrm}
                      </td>
                      <td className={td}>
                        <Pill tone={w.consec >= 6 ? 'bad' : 'warn'}>{w.consec} days</Pill>
                      </td>
                      <td className={td}>
                        <Pill tone='bad'>Immediate review</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Banner tone='info'>No WTR breaches this period.</Banner>
          )}
        </Panel>
        <Panel title='Hours Compliance' sub='EU Working Time Directive limits'>
          <div className={dataTable.wrap}>
            <table className={dataTable.table}>
              <thead>
                <tr>
                  <Th>Worker</Th>
                  <Th num>Wk Hrs</Th>
                  <Th num>OT Hrs</Th>
                  <Th center>48h avg</Th>
                  <Th center>56h cap</Th>
                </tr>
              </thead>
              <tbody>
                {[...workers]
                  .sort((a, b) => b.shiftHrs - a.shiftHrs)
                  .map((w) => (
                    <tr key={w.init} {...row(w)}>
                      <td className={cn(td, 'font-semibold')}>{w.name}</td>
                      <td
                        className={cn(
                          td,
                          num,
                          w.shiftHrs > 55
                            ? 'font-semibold text-destructive'
                            : w.shiftHrs > 48
                              ? 'font-semibold text-warning'
                              : 'text-muted-foreground',
                        )}
                      >
                        {w.shiftHrs}h
                      </td>
                      <td className={cn(td, num)}>{w.otHrs}h</td>
                      <td className={cn(td, 'text-center')}>
                        <Pill tone={w.shiftHrs > 48 ? 'warn' : 'good'}>{w.shiftHrs > 48 ? 'Over' : 'OK'}</Pill>
                      </td>
                      <td className={cn(td, 'text-center')}>
                        <Pill tone={w.shiftHrs > 55 ? 'bad' : 'good'}>{w.shiftHrs > 55 ? 'Near' : 'OK'}</Pill>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </Grid>
      <Grid cols={2} className='mb-5'>
        <Panel title='Driving Compliance' sub='EU tachograph: 9h/day · 56h/week'>
          <div className={dataTable.wrap}>
            <table className={dataTable.table}>
              <thead>
                <tr>
                  <Th>Driver</Th>
                  <Th num>Wk km</Th>
                  <Th num>Drive Days</Th>
                  <Th center>Daily 9h</Th>
                  <Th center>Weekly 56h</Th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((w) => {
                  const dailyAvg = w.driveDays ? +(w.driveKm / w.driveDays).toFixed(0) : 0;
                  const dailyRisk = dailyAvg > 500;
                  const weekRisk = w.driveKm > 700;
                  return (
                    <tr key={w.init} {...row(w)}>
                      <td className={cn(td, 'font-semibold')}>{w.name}</td>
                      <td
                        className={cn(
                          td,
                          num,
                          weekRisk ? 'text-destructive' : w.driveKm > 500 ? 'text-warning' : 'text-muted-foreground',
                        )}
                      >
                        {w.driveKm}
                      </td>
                      <td className={cn(td, num)}>{w.driveDays}</td>
                      <td className={cn(td, 'text-center')}>
                        <Pill tone={dailyRisk ? 'bad' : 'good'}>{dailyRisk ? 'Review' : 'OK'}</Pill>
                      </td>
                      <td className={cn(td, 'text-center')}>
                        <Pill tone={weekRisk ? 'bad' : 'good'}>{weekRisk ? 'Breach' : 'OK'}</Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel title='FRMS Authorisation Log' sub='Fatigue Risk Management System sign-offs'>
          <div className={dataTable.wrap}>
            <table className={dataTable.table}>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Worker</Th>
                  <Th>Type</Th>
                  <Th>Auth By</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {FRMS_LOG.map((r, i) => (
                  <tr key={i} className={dataTable.tr}>
                    <td className={td}>{r.date}</td>
                    <td className={td}>{r.worker}</td>
                    <td className={td}>{r.type}</td>
                    <td className={td}>{r.by}</td>
                    <td className={td}>
                      <Pill tone={r.tone}>{r.status}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </Grid>
    </>
  );
};

export const ComplianceScreen: React.FC = () => {
  const d = useDash();
  const workers = scopedWorkers(d);

  if (d.drillScope === 'company') {
    const allW = wArr();
    return (
      <div>
        <ComplianceKpis workers={allW} />
        <AiInsight>{genInsight('compliance', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
        <SecHeader className='mt-4'>Compliance by Region — click to view detail</SecHeader>
        <Grid cols={2} className='mb-4'>
          {Object.entries(REGIONS).map(([rid, reg]) => {
            const rw = workersInRegion(rid);
            const rWtr = rw.filter((w) => w.wtrFlag).length;
            const rOver48 = rw.filter((w) => w.shiftHrs > 48).length;
            const rOver55 = rw.filter((w) => w.shiftHrs > 55).length;
            const rDrive = rw.filter((w) => w.driveKm > 700).length;
            const critical = rWtr > 0 || rOver55 > 0 || rDrive > 0;
            const worst: Tone = critical ? 'bad' : rOver48 > 0 ? 'warn' : 'good';
            const worstLabel = critical ? 'Action required' : rOver48 > 0 ? 'Monitor' : 'Compliant';
            return (
              <Panel key={rid} className='cursor-pointer transition-colors hover:bg-muted/40'>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() => d.drillRegion(rid)}
                  onKeyDown={(e) => e.key === 'Enter' && d.drillRegion(rid)}
                >
                  <div className='mb-3 flex items-center gap-2'>
                    <span className='h-2.5 w-2.5 shrink-0 rounded-full' style={{ background: reg.color }} />
                    <span className='font-semibold'>{reg.name}</span>
                    <span className={cn('ml-auto text-[11px] font-semibold', toneText[worst])}>{worstLabel}</span>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <StatTile value={rWtr} label='WTR breaches' tone={rWtr ? 'bad' : 'muted'} alert={rWtr > 0} />
                    <StatTile value={rOver48} label='>48h/wk' tone={rOver48 ? 'warn' : 'muted'} alert={rOver48 > 0} />
                    <StatTile value={rOver55} label='>55h/wk' tone={rOver55 ? 'bad' : 'muted'} />
                    <StatTile value={rDrive} label='EU drive risk' tone={rDrive ? 'bad' : 'muted'} />
                  </div>
                </div>
              </Panel>
            );
          })}
        </Grid>
        <ComplianceTables workers={allW} />
      </div>
    );
  }

  if (d.drillScope === 'region' && d.activeRegion) {
    return (
      <div>
        <RegionPicker />
        <ComplianceKpis workers={workers} />
        <AiInsight>
          {genInsight('compliance', workers, { regionId: d.activeRegion, scopeName: REGIONS[d.activeRegion].name })}
        </AiInsight>
        <div className='mt-4'>
          <ComplianceTables workers={workers} />
        </div>
      </div>
    );
  }

  if (d.drillScope === 'group' && d.activeGroup !== null) {
    return (
      <div>
        <RegionPicker resetGroup compact />
        <GroupPicker regionId={GROUPS[d.activeGroup].region} />
        <ComplianceKpis workers={workers} />
        <AiInsight>{genInsight('compliance', workers, { scopeName: GROUPS[d.activeGroup].name })}</AiInsight>
        <div className='mt-4'>
          <ComplianceTables workers={workers} />
        </div>
      </div>
    );
  }

  const w = d.activeWorker ? WORKERS[d.activeWorker] : undefined;
  return (
    <div>
      <WorkerPicker />
      {w && (
        <>
          <ComplianceKpis workers={[w]} />
          <AiInsight>{genInsight('compliance', [w], { scopeName: w.name })}</AiInsight>
          <div className='mt-4'>
            <ComplianceTables workers={[w]} />
          </div>
        </>
      )}
    </div>
  );
};
